
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { formatReadingTime } from '@/components/features/academy/academy.helpers';
import { GamingGuidesDetailPageProps } from '@/components/features/academy/academy.types';
import { BaseLayout } from '@/components/templates';
import { getOpenGraphProps } from '@/components/utils/seo/get-open-graph-props';
import { Seo } from '@/components/utils/seo/seo-component';
import { withUrql } from '@/config/graphql';
import { URLS } from '@/config/urls';
import { getArticleUrl, getCategoryUrl } from '@/utils/academy';
import { getGamesUrl } from '@/utils/games';
import { canonicalUrl as getCanonicalUrl } from '@/utils/seo';
import { Box, Button, Container, Heading, Link, Text } from '@chakra-ui/react';
import { contentfulClient } from '@cms/client';
import { GamingGuidePageDocument, GamingGuidePageQuery, } from '@cms/graphql/gamingGuidesDetailPage.generated';
import { isExternalUrl } from '@meritcircle/ui';
import { Breadcrumbs, LazyImage, LinkBox, LinkBoxList } from '@meritcircle/ui/components';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { BreadcrumbJsonLd } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
const Page: NextPage<PageProps> = ({ linkedSeoTags, lastUpdate, title, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords, description, mainImage, articles, urlSlug, }) => {
    const { t } = useTranslation('gaming-guides');
    const router = useRouter();
    const breadcrumbs = [
        { title: t('title'), href: URLS.GAMING_GUIDES },
        { title: title, href: getCategoryUrl(urlSlug) },
    ];
    const canonicalUrl = getCanonicalUrl(router);
    return (<>
      {mainImage && (<Box position="absolute" height="50vh" width="100%" _after={{
                content: '""',
                width: '100%',
                height: '100%',
                position: 'absolute',
                background: 'linear-gradient(1.07deg, #000000 20.53%, rgba(0, 0, 0, 0) 42.04%)'
            }}>
          <LazyImage src={mainImage.url} placeholderUrl={mainImage.url} alt={mainImage.description || mainImage.title || `${title} image`} position="absolute" objectFit="cover" width="100%" height="100%" maxWidth="none" opacity="0.1"/>
        </Box>)}
      <BaseLayout>
        <Seo title={linkedSeoTags?.title || metaTitle || title} description={linkedSeoTags?.description || metaDescription || description} nofollow={linkedSeoTags?.noFollow || noFollow} noindex={linkedSeoTags?.noIndex || noIndex} keywords={linkedSeoTags?.keywords || keywords} lastUpdate={lastUpdate} canonical={canonicalUrl} openGraph={getOpenGraphProps({
            title: linkedSeoTags?.title || metaTitle,
            description: linkedSeoTags?.description || metaDescription,
            url: canonicalUrl,
            image: ogImage
        })}/>
        <BreadcrumbJsonLd itemListElements={breadcrumbs.map((breadcrumb, idx) => ({
            position: idx + 1,
            name: breadcrumb.title,
            item: breadcrumb.href
        }))}/>
        <Container pb="2xl">
          <Breadcrumbs breadcrumbs={breadcrumbs} mt={{ base: 'md', md: 'lg' }}/>
          <Box maxWidth={750} pt={{ base: 10, md: 20 }} pb={{ base: 14, md: 24 }}>
            <Text variant="muted" size="xs" mb={2}>
              {t('title-label')}
            </Text>
            <Heading as="h1" variant="orangeLeftRight" size="2xl" mb={{ base: 4, md: 6 }}>
              {title}
            </Heading>
            <Text>{description}</Text>
            <NextLink href={getGamesUrl(urlSlug)} passHref legacyBehavior>
              <Button variant="secondary" mt={8} as={Link} isExternal={isExternalUrl(getGamesUrl(urlSlug))} data-testid="gaming-guide-game-goto-button">
                {t('cta-label')}
              </Button>
            </NextLink>
          </Box>
          <LinkBoxList>
            {articles.map(({ overviewTitle, overviewDescription, urlSlug, readingTime }) => (<LinkBox href={getArticleUrl(urlSlug)} key={urlSlug}>
                <Heading as="h2" size="h4">
                  {overviewTitle}
                </Heading>
                {readingTime && (<Text variant="muted" size="xs" mt={2} textTransform="uppercase">
                    {formatReadingTime(readingTime, t('common:date.minute'), t('common:date.minutes'))}
                  </Text>)}
                <Text mt={4} variant="muted">
                  {overviewDescription}
                </Text>
              </LinkBox>))}
          </LinkBoxList>
        </Container>
      </BaseLayout>
    </>);
};
const getServerSideProps: GetServerSideProps<GamingGuidesDetailPageProps> = async (ctx) => {
    const slug = `${ctx?.params?.slug}`;
    const contentfulResponse = await contentfulClient
        .query<GamingGuidePageQuery>(GamingGuidePageDocument, { slug })
        .toPromise();
    const game = contentfulResponse?.data?.pageGameCollection.items[0];
    if (!game) {
        return {
            notFound: true
        };
    }
    const { mainImage, linkedSeoTags, linkedAcademyCategory, urlSlug } = game;
    if (!linkedAcademyCategory) {
        return {
            notFound: true
        };
    }
    const { title, description, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords } = linkedAcademyCategory;
    return {
        props: {
            linkedSeoTags,
            lastUpdate: `${new Date()}`,
            title,
            description,
            metaTitle,
            metaDescription,
            ogImage,
            noFollow,
            noIndex,
            keywords,
            mainImage,
            urlSlug,
            articles: linkedAcademyCategory.childArticlesCollection.items
        }
    };
};
export default withUrql(Page);

    async function __Next_Translate__getServerSideProps__1973b3b2b77__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/gaming-guides/[slug]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1973b3b2b77__ as getServerSideProps }
  